import { type ComponentType, lazy } from 'react'
import type { PanelType } from './panel-state'

/**
 * Record-panel registry - the seam module tracks (PLAN-04/07/08) use to mount
 * their own record panels into the shell's side-panel host without editing the
 * host's switch. A module registers a component for a `PanelType`; the host
 * looks it up and renders it (passing the `panelId`), falling back to
 * `PanelNotReady` when nothing is registered.
 *
 * Registration happens as a side effect of importing the module's panel barrel
 * (the route files import it). Idempotent: re-registering the same type replaces
 * the prior component (Storybook/HMR-safe).
 *
 * (DR-11 - this is the panel mount-point seam PLAN-00 should canonicalise; added
 * by PLAN-04 to keep the shell generic.)
 */

export interface RecordPanelProps {
  panelId?: string
  onClose: () => void
  /** Present when this panel was opened as a cross-reference from another
   * record (deeper than the root). Render a Back button that calls it to return
   * to the previous record. Undefined at the root of the panel stack. */
  onBack?: () => void
  /** `create` opens the panel as a blank draft (no `panelId`); the panel renders
   * a create form and, on save, navigates to the new record (view mode). When
   * omitted the panel shows/edits the existing `panelId` record. */
  mode?: 'create' | 'view'
}

const registry = new Map<PanelType, ComponentType<RecordPanelProps>>()

// Registration version - lets the panel host re-render when a register-panels
// module that was loaded asynchronously (dynamic import at boot) lands after
// the first paint. Plain number + listener set, consumed via
// useSyncExternalStore in the host.
let version = 0
const listeners = new Set<() => void>()

function notify() {
  version += 1
  for (const listener of listeners) listener()
}

/** Subscribe to panel (un)registration; returns the unsubscribe. */
export function subscribeRecordPanels(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Monotonic registration counter (useSyncExternalStore snapshot). */
export function getRecordPanelVersion(): number {
  return version
}

export function registerRecordPanel(
  type: PanelType,
  component: ComponentType<RecordPanelProps>,
): void {
  registry.set(type, component)
  notify()
}

/**
 * Register a record panel WITHOUT pulling its component into the boot bundle.
 * The register-panels side-effect modules are imported at boot (lib/boot.ts),
 * so a static component import there would drag every panel (and its whole
 * dependency graph - grids, editors, activity feed) into the entry chunk. This
 * variant stores a `React.lazy` shell instead: registration stays synchronous
 * (deep links resolve on first render) while the component code loads on first
 * open. The host renders registered panels inside a Suspense boundary.
 */
export function registerLazyRecordPanel(
  type: PanelType,
  loader: () => Promise<{ default: ComponentType<RecordPanelProps> }>,
): void {
  registry.set(type, lazy(loader))
  notify()
}

export function getRecordPanel(type: PanelType): ComponentType<RecordPanelProps> | undefined {
  return registry.get(type)
}
